import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ImageEnum } from '../../../../shared/utils/image.enum';
import { AuthService } from '../../../auth/services/auth.service';
import { ExamLoadingScreenData, ExamResultState } from '../../model/test-series.model';
import { ExamSessionService } from '../../services/exam-session.service';
import { TestSeriesService } from '../../services/test-series.service';
import { TitleCasePipe } from '../../../../shared/pipes/title-case-pipe';

@Component({
  selector: 'app-exam-loading',
  imports: [TitleCasePipe],
  templateUrl: './exam-loading.html',
  styleUrl: './exam-loading.css',
})
export class ExamLoadingComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly examSessionService = inject(ExamSessionService);
  private readonly testSeriesService = inject(TestSeriesService);
  private readonly toastr = inject(ToastrService);
  private readonly authService = inject(AuthService);
  private redirectTimer: ReturnType<typeof setTimeout> | null = null;

  protected readonly imageEnum = ImageEnum;
  protected readonly candidateName = signal('');
  private readonly defaultScreenData: ExamLoadingScreenData = {
    candidateName: '',
    rollNumber: '12345678',
    heading: 'Preparing Your Examination...',
    description: 'Please wait a moment while we load your exam.',
    statusMessage: 'Please wait...',
    redirectDelayMs: 3000,
  };
  private readonly defaultExamResultState: ExamResultState = {
    examTitle: 'National NEET-PG Mock 2024 (New NEET-PG Pattern)',
    examMeta: 'Full Mock Test · Jul 16, 2026',
    submittedAt: new Date().toISOString(),
    candidateName: 'Dr. User',
    candidateInitials: 'U',
    performanceLabel: 'Great Performance!',
    submissionSummary: {
      totalQuestions: 200,
      totalAttempted: 36,
      totalUnattempted: 164,
      markedForReview: 8,
      timeTaken: '3h 27m',
      remainingTime: '71 min',
    },
    sectionSummaries: [],
  };
  protected screenData: ExamLoadingScreenData = this.defaultScreenData;
  protected examResultState: ExamResultState = this.defaultExamResultState;
  protected isSubmitMode = false;
  /** Submit mode for an exit that only saves progress (IN_PROGRESS) — the attempt can still be resumed. */
  protected isSaveProgressMode = false;

  ngOnInit(): void {
    this.authService.getUserDetails().subscribe({
      next: (res) => {
        const candidateName = res?.data?.name?.trim() || '';
        this.candidateName.set(candidateName);
        this.screenData = {
          ...this.screenData,
          candidateName,
        };
        this.examResultState = {
          ...this.examResultState,
          candidateName,
        };
      },
      error: () => {
        this.candidateName.set('');
      },
    });

    const testId = this.route.snapshot.paramMap.get('id');
    const mode = this.route.snapshot.queryParamMap.get('mode');
    this.isSubmitMode = mode === 'submit';

    if (this.isSubmitMode) {
      this.examResultState = {
        ...(this.examSessionService.pendingExamResultState() ?? this.defaultExamResultState),
        candidateName: this.examResultState.candidateName,
      };
      const submitPayload = this.examSessionService.pendingSubmitPayload();
      this.isSaveProgressMode = submitPayload?.status === 'IN_PROGRESS';

      this.screenData = {
        ...this.screenData,
        heading: this.isSaveProgressMode ? 'Saving Your Progress...' : 'Submitting Your Responses...',
        description: this.isSaveProgressMode
          ? 'Please wait. Do not close or refresh the browser. Your answers are being saved so you can resume the exam from the dashboard.'
          : 'Please wait. Do not close or refresh the browser. We are securely uploading your answers.',
        statusMessage: 'Please wait...',
      };

      if (!testId || !submitPayload) {
        void this.router.navigate(['/test-series']);
        return;
      }

      // Submit API is no longer needed — TEST_SUBMITTED over the event
      // WebSocket finalizes the attempt server-side. Kept for reference.
      // this.testSeriesService.submitExam(submitPayload).subscribe({
      //   next: () => {
      //     this.examSessionService.clearPendingSubmission();
      //     this.redirectTimer = setTimeout(() => {
      //       void this.navigateToSubmittedScreen(testId);
      //     }, this.screenData.redirectDelayMs);
      //   },
      //   error: () => {
      //     void this.router.navigate(['/test-series']);
      //   },
      // });
      this.examSessionService.clearPendingSubmission();
      this.redirectTimer = setTimeout(() => {
        void this.navigateToSubmittedScreen(testId);
      }, this.screenData.redirectDelayMs);

      return;
    }

    if (!testId) {
      void this.router.navigate(['/test-series']);
      return;
    }

    this.loadExamQuestions(testId);
  }

  ngOnDestroy(): void {
    if (this.redirectTimer) {
      clearTimeout(this.redirectTimer);
    }
  }

  private loadExamQuestions(testId: string): void {
    this.examSessionService.ensurePreparedQuestionPaperState(testId).subscribe({
      next: (preparedState) => {
        if (!preparedState.questions.length) {
          this.examSessionService.clearPreparedQuestionPaperState();
          this.toastr.warning('No questions are available for this exam.');
          void this.router.navigate(['/test-series']);
          return;
        }

        this.redirectTimer = setTimeout(() => {
          void this.router.navigate(['/test-series', testId, 'exam-recommendation'], { replaceUrl: true });
        }, this.screenData.redirectDelayMs);
      },
      error: () => {
        void this.router.navigate(['/test-series']);
      },
    });
  }

  private async navigateToSubmittedScreen(testId: string): Promise<void> {
    await this.exitFullscreenIfActive();

    await this.router.navigate(['/test-series', testId, 'examination-submitted'], {
      state: { examResultState: this.examResultState },
      replaceUrl: true,
    });
  }

  private async exitFullscreenIfActive(): Promise<void> {
    if (!document.fullscreenElement || !document.exitFullscreen) {
      return;
    }

    try {
      await document.exitFullscreen();
    } catch {
      // Keep the submit flow moving even if the browser blocks fullscreen exit here.
    }
  }
}
